import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { CreditCard, Copy, Check, Eye, MousePointerClick, UserPlus, ExternalLink, Loader2, Save, Palette } from "lucide-react";

export default function CardEditorPage() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [form, setForm] = useState({ title: "", company: "", photoUrl: "", bio: "", links: { website: "", linkedin: "", phone: "" } });
  const [stats, setStats] = useState({ views: 0, totalClicks: 0, leads: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/card"), api.get("/card/analytics")])
      .then(([c, a]) => {
        setSlug(c.data.slug);
        setForm({ title: c.data.title || "", company: c.data.company || "", photoUrl: c.data.photoUrl || "", bio: c.data.bio || "", links: c.data.links || { website: "", linkedin: "", phone: "" } });
        setStats(a.data);
      })
      .catch(() => toast.error("Impossible de charger votre carte"))
      .finally(() => setLoading(false));
  }, []);

  const url = slug ? `${window.location.origin}/card/${slug}` : "";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setLink = (k) => (e) => setForm((f) => ({ ...f, links: { ...f.links, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/card", form);
      toast.success("Carte mise à jour ✅");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Lien copié ✨"); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Copie impossible"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="card-editor-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center"><CreditCard className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Ma carte de visite digitale</h1>
          <p className="text-sm text-muted-foreground">Partagez votre carte, laissez le prospect enregistrer votre contact et vous laisser les siens.</p>
        </div>
      </div>

      <Button variant="outline" onClick={() => navigate("/branding")} data-testid="open-branding-button" className="mt-4 rounded-xl"><Palette className="w-4 h-4 mr-2" /> Identité de marque de l'équipe</Button>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm space-y-4">
          <Field label="Titre / poste" testid="card-edit-title" value={form.title} onChange={set("title")} placeholder="Responsable commercial" />
          <Field label="Entreprise" testid="card-edit-company" value={form.company} onChange={set("company")} placeholder="Kallitag" />
          <Field label="Photo (URL)" testid="card-edit-photo" value={form.photoUrl} onChange={set("photoUrl")} placeholder="https://…/photo.jpg" />
          <div className="space-y-1.5">
            <Label className="text-muted-foreground">Bio courte</Label>
            <Textarea data-testid="card-edit-bio" value={form.bio} onChange={set("bio")} rows={2} placeholder="Une phrase sur vous / votre offre" />
          </div>
          <Field label="Site web" testid="card-edit-website" value={form.links.website} onChange={setLink("website")} placeholder="kallitag.fr" />
          <Field label="LinkedIn" testid="card-edit-linkedin" value={form.links.linkedin} onChange={setLink("linkedin")} placeholder="linkedin.com/in/…" />
          <Field label="Téléphone" testid="card-edit-phone" value={form.links.phone} onChange={setLink("phone")} placeholder="+33 6 12 34 56 78" />
          <Button onClick={save} disabled={saving} data-testid="card-save-button" className="w-full rounded-xl h-11">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>

        {/* QR + analytics */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm flex flex-col items-center">
            {url && <div className="bg-white p-4 rounded-2xl border border-border" data-testid="card-qr"><QRCodeCanvas value={url} size={180} level="M" /></div>}
            <div className="w-full mt-4 flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2">
              <span className="text-xs text-muted-foreground truncate flex-1" data-testid="card-url">{url}</span>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 rounded-lg" onClick={copy} data-testid="card-copy-button">{copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}</Button>
            </div>
            <Button variant="outline" onClick={() => window.open(url, "_blank")} data-testid="card-preview-button" className="w-full rounded-xl mt-2"><ExternalLink className="w-4 h-4 mr-2" /> Aperçu de ma carte</Button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-heading font-bold mb-3">Engagement de ma carte</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat icon={Eye} value={stats.views} label="Vues" testid="card-stat-views" />
              <Stat icon={MousePointerClick} value={stats.totalClicks} label="Clics" testid="card-stat-clicks" />
              <Stat icon={UserPlus} value={stats.leads} label="Leads" testid="card-stat-leads" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground">{label}</Label>
      <Input data-testid={testid} value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

function Stat({ icon: Icon, value, label, testid }) {
  return (
    <div data-testid={testid}>
      <Icon className="w-4 h-4 mx-auto text-muted-foreground" />
      <div className="font-heading text-2xl font-extrabold mt-1 tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
